import { useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { boxInput, currentMonth, inr, paidDaysFrom } from "../../lib/format";

const DEFAULT_EARN = [{ label: "Basic", amount: "" }, { label: "HRA", amount: "" }, { label: "Other allowances", amount: "" }];
const DEFAULT_DED = [{ label: "PF", amount: "" }, { label: "ESIC", amount: "" }, { label: "Professional tax", amount: "" }];
const sum = (rows) => rows.reduce((a, r) => a + (Number(r.amount) || 0), 0);

const Lines = ({ title, rows, setRows, prefix }) => (
  <div>
    <p className="overline text-[var(--signal)]">{title}</p>
    <div className="mt-3 space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-[1fr_120px_auto] gap-2">
          <input value={r.label} onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} className={boxInput} data-testid={`${prefix}-label-${i}`} />
          <input type="number" min="0" step="0.01" value={r.amount} placeholder="0" onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))} className={boxInput} data-testid={`${prefix}-amount-${i}`} />
          <button type="button" onClick={() => setRows(rows.filter((_, j) => j !== i))} className="p-2 text-[var(--bone-2)] hover:text-red-300" data-testid={`${prefix}-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
    </div>
    <button type="button" onClick={() => setRows([...rows, { label: "", amount: "" }])} className="mt-2 flex items-center gap-1 text-sm text-[var(--bone-2)] hover:text-[var(--signal)]" data-testid={`${prefix}-add`}>
      <Plus className="h-4 w-4" /> Add line
    </button>
  </div>
);

export const PayslipForm = ({ employeeId, onSaved }) => {
  const [month, setMonth] = useState(currentMonth());
  const [days, setDays] = useState({ working_days: 26, paid_days: 26 });
  const [earn, setEarn] = useState(DEFAULT_EARN);
  const [ded, setDed] = useState(DEFAULT_DED);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const fillDays = async () => {
    try {
      const { data } = await api.get(`/admin/employees/${employeeId}/attendance`, { params: { month } });
      setDays((d) => ({ ...d, paid_days: paidDaysFrom(data) }));
      toast.success("Paid days filled from attendance");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    const clean = (rows) => rows.filter((r) => r.label.trim()).map((r) => ({ label: r.label.trim(), amount: Number(r.amount) || 0 }));
    setBusy(true);
    try {
      await api.post(`/admin/employees/${employeeId}/payslips`, {
        month, working_days: Number(days.working_days), paid_days: Number(days.paid_days), earnings: clean(earn), deductions: clean(ded), note,
      });
      toast.success("Salary slip saved");
      onSaved();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-6 border border-white/10 p-5 md:p-6" data-testid="payslip-form">
      <div className="grid gap-3 sm:grid-cols-[1fr_1fr_1fr_auto] sm:items-end">
        <label className="text-sm"><span className="overline text-[var(--bone-2)]">Month</span>
          <input type="month" required value={month} onChange={(e) => setMonth(e.target.value)} className={`${boxInput} mt-1`} data-testid="payslip-month-input" /></label>
        <label className="text-sm"><span className="overline text-[var(--bone-2)]">Working days</span>
          <input type="number" min="0" max="31" step="0.5" required value={days.working_days} onChange={(e) => setDays({ ...days, working_days: e.target.value })} className={`${boxInput} mt-1`} data-testid="payslip-working-days-input" /></label>
        <label className="text-sm"><span className="overline text-[var(--bone-2)]">Paid days</span>
          <input type="number" min="0" max="31" step="0.5" required value={days.paid_days} onChange={(e) => setDays({ ...days, paid_days: e.target.value })} className={`${boxInput} mt-1`} data-testid="payslip-paid-days-input" /></label>
        <button type="button" onClick={fillDays} className="border border-white/20 px-3 py-2 text-sm hover:border-[var(--signal)]" data-testid="payslip-fill-days-button">From attendance</button>
      </div>
      <div className="grid gap-8 md:grid-cols-2">
        <Lines title="Earnings" rows={earn} setRows={setEarn} prefix="earn" />
        <Lines title="Deductions" rows={ded} setRows={setDed} prefix="ded" />
      </div>
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)" className={boxInput} data-testid="payslip-note-input" />
      <div className="flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-5">
        <p className="text-sm text-[var(--bone-2)]" data-testid="payslip-totals">
          Gross {inr(sum(earn))} − Deductions {inr(sum(ded))} = <span className="font-display text-2xl font-black text-[var(--signal)]">{inr(sum(earn) - sum(ded))}</span>
        </p>
        <button type="submit" disabled={busy} className="flex items-center gap-2 rounded-full bg-[var(--signal)] px-6 py-3 font-semibold text-[var(--ink)] disabled:opacity-60" data-testid="payslip-save-button">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save salary slip
        </button>
      </div>
      <p className="text-xs text-[var(--bone-2)]">Saving a slip for a month that already has one replaces it.</p>
    </form>
  );
};
